import React, { useRef, useState } from 'react';
import { Alert, Button, Card, Form, Row } from 'react-bootstrap';
import { ArrowUp } from 'react-feather';
import { Link } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { CSVLink } from 'react-csv';

import K2Api from 'lib/ajax/K2Api';
import { useK2Loader } from 'hooks/useK2Loader';
import Loader from 'components/Loader';
import Breadcrumb from "components/LM/common/Breadcrumb";

const SOURCE = 'deepblue';
const k2Api = new K2Api();

const MULTI_VALUE_EXAMPLES = {
	label: 'label1;label2',
	online_media: '123;456',
	print_media: '123;456',
	profile: 'url1;url2'
};

function MultiValueNote({ example }) {
	return <>Yes, e.g. <strong>{example}</strong></>;
}

function BatchRequestUpload(props) {
	const [file, setFile] = useState(null);
	const [name, setName] = useState('');

	const [uploading, setUploading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState(null);
	const fileInputRef = useRef(null);

	const [loadingLabels, labelsResponse] = useK2Loader('/labels');
	if (loadingLabels) return <Loader />;

	const labels = labelsResponse.map(label => label.name);

	const onFileChange = (ev) => {
		const selected = ev.target.files[0] ?? null;
		setSuccess(false);
		setError(null);
		setFile(selected);
	}

	const onSubmit = async () => {
		setError(null);
		setSuccess(false);
		const formData = new FormData();
		formData.append('file', file);
		setUploading(true);

		const query = new URLSearchParams({ source: SOURCE });
		if (name) query.set('name', name);

		k2Api.post(`/voices/batch-request?${query.toString()}`, formData)
			.then((response) => {
				setSuccess(true);
				setTimeout(() => props.history.push(`/voices/new-import/status/${response.data.id}`), 1000);
			})
			.catch((err) => {
				setError(formatUploadError(err));
				// Force the user to pick the file again before re-uploading
				setFile(null);
				if (fileInputRef.current) fileInputRef.current.value = '';
			})
			.finally(() => setUploading(false));
	}

	return (
		<>
			<Breadcrumb items={['Voices', 'New Mass Import']} />
			<Card>
				<Card.Header>
					{success && <AlertMessage variant="success" message="File uploaded successfully" />}
					{error && <AlertMessage variant="danger" message={error} />}
					<Form>
						<Form.Group as={Row} className="mb-2">
							<Form.Control
								type="text"
								placeholder="Batch request name (optional)"
								value={name}
								onChange={ev => setName(ev.target.value)}
							/>
						</Form.Group>
						<Form.Group as={Row} className="file-upload" id="formFile">
							<Form.Control type="file" accept=".csv" onChange={onFileChange} id="uploadFile" />
							<Button className="button-upload" disabled={!file || uploading} onClick={onSubmit}>
								Upload <ArrowUp width={22} height={22} />
							</Button>
						</Form.Group>
					</Form>
					<br />
					<Link to="/voices/new-import/status">View uploaded files status</Link>
				</Card.Header>
				<Card.Body>
					<p>The file must be a <strong>.csv</strong> file separated by comma and encoded as <strong>utf-8</strong></p>
					<p>The first row indicates the column contents, the process will read these names in order to get the contents</p>
					<p>The accepted column names are:</p>
					<Table striped bordered>
						<thead>
							<tr>
								<th>Column name</th>
								<th>Note</th>
								<th>Multiple values (separate with <strong>;</strong> )</th>
							</tr>
						</thead>
						<tbody>
							<tr><td>id</td><td>voice Id</td><td>No</td></tr>
							<tr><td>name</td><td>name of the voice</td><td>No</td></tr>
							<tr><td>type</td><td>allowed values are:<br /> media, brand, influencer, celebrity</td><td>No</td></tr>
							<tr><td>country_iso</td><td>two letters country code uppercase e.g.   GB, FR, ES, IT….</td><td>No</td></tr>
							<tr><td>label</td><td>allowed values are: <br />{labels.join(', ')}</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.label} /></td></tr>
							<tr><td>online_media</td><td>online media id</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.online_media} /></td></tr>
							<tr><td>print_media</td><td>print media id</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.print_media} /></td></tr>
							<tr><td>twitter</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>facebook</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>instagram</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>youtube</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>tiktok</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>xiaohongshu (LittleRedBook)</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>douyin</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>weibo</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>wechat</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
							<tr><td>bilibili</td><td>profile url</td><td><MultiValueNote example={MULTI_VALUE_EXAMPLES.profile} /></td></tr>
						</tbody>
					</Table>

					<p>You can download an <SampleDownload /> to use as a template</p>
					<br />
					<h4>How does it work</h4>
					<p>With this feature, you can import new voices or update the existing ones</p>

					<ul>
						<li>New Voice</li>
						<ul>
							<li>one of profile_url <span className="text-danger">*</span></li>
							<li>name</li>
							<li>type</li>
							<li>country_iso</li>
							<li>label</li>
						</ul>
					</ul>

					<ul>
						<li>Update Voice</li>
						<ul>
							<li>id <span className="text-danger">*</span></li>
							<li>one of profile_url <span className="text-danger">*</span></li>
							<li>name</li>
							<li>type</li>
							<li>country_iso</li>
							<li>label</li>
						</ul>
					</ul>

					<p><span className="text-danger">*</span> Mandatory field</p>
					<br />
					<h4>FAQ</h4>
					<p><b>Can I use the same file to update the existing voices and for adding new ones? </b>

						Yes, it's not necessary to split in two CSV.
					</p>

					<p><b>I have to update the country for 1000 voices, but I have only the Instagram URL. Do I have to fill type and label? </b>

						No, it's not needed! You can leave empty the column that you don't want to update.</p>

					<p><b>I need to insert the TikTok profiles for 500 voices, but I have only the voice name! Can I do it? </b>

						No, with the voice name, it’s easy to create duplicates. You need the <b>id</b> or one of the existing profile URLs.
					</p>
				</Card.Body>
			</Card>
		</>
	);
}

function formatUploadError(err) {
	if (!err?.response) {
		return "Upload failed: no response from the server. Please try again.";
	}
	const validation = err?.response?.data?.error?.validation;
	if (Array.isArray(validation)) {
		return validation
			.flatMap(entry =>
				Object.entries(entry.fields ?? {}).map(([field, message]) =>
					entry.row === null ? `${field}: ${message}` : `Row ${entry.row + 1} — ${field}: ${message}`
				)
			)
			.join("\n");
	}
	return err?.response?.data?.message ?? err?.message ?? "Upload failed";
}

function AlertMessage({ variant, message }) {
	const body = message.split("\n").map((line, index) => <p key={index} className="mb-0">{line}</p>);
	return <Alert variant={variant}><div className="m-2">{body}</div></Alert>;
}

function SampleDownload() {
	const data = ['id', 'name', 'country_iso', 'label', 'type', 'online_media', 'print_media', 'twitter', 'facebook', 'instagram', 'youtube', 'tiktok', 'xiaohongshu', 'douyin', 'weibo', 'wechat', 'bilibili'];
	return <CSVLink data={[data]} separator={","} filename={"voices_example.csv"}>example</CSVLink>
}

export default BatchRequestUpload;
